import * as React from 'react';
import Table from 'antd/lib/table';
import Pagination from 'antd/lib/pagination';
import Result from 'antd/lib/result';
import Button from 'antd/lib/button';
import Skeleton from 'antd/lib/skeleton';
import Empty from 'antd/lib/empty';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import { MehOutlined } from '@ant-design/icons';
import { SummaryViewContextValue } from '@/utils/hooks/useSummaryView';
import { TabularLayoutType } from '@/types/summaryView';
import { makeDraggable } from '@/helpers/summaryViewHelpers';
import { antGrey } from '@/components/styles/globalStyles';
import type { DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
    arrayMove,
    horizontalListSortingStrategy,
    verticalListSortingStrategy,
    SortableContext,
} from '@dnd-kit/sortable';
import {
    DragIndexContext,
    DragIndexState,
    TableHeaderCell,
    TableBodyCell,
    TableBodyRow,
} from '@/components/orgSummary/DraggableTableComponents';

interface SummaryTableProps {
    summary: SummaryViewContextValue;
}

export const SummaryTable: React.FC<SummaryTableProps> = ({ summary }) => {
    const {
        preferences,
        error: { shouldDisplayFetchFailed, setShouldDisplayFetchFailed },
        refetchCurrentPage,
        loading: { isLoading },
        tableData,
        pagination: { currentPage, pageSize, setCurrentPage },
        summaryViewResponse,
        updatePreferences,
        setStandardRowOrder,
        selectedTotalCount,
        availableTotalCount,
        isLoadingCount,
    } = summary;

    const { tabularLayout } = preferences;
    const { standardColumns, compactConfig, filteredRows } = tableData;

    const isCompactLayout = tabularLayout === TabularLayoutType.Compact;
    const tableDataSource = isCompactLayout ? compactConfig.rows : filteredRows;
    const hasActualData = filteredRows.length > 0;
    const hasFetchCompleted = summaryViewResponse !== undefined || shouldDisplayFetchFailed;
    const showSkeletonInEmptyState = !hasFetchCompleted || (!hasActualData && isLoading);
    const [dragIndex, setDragIndex] = React.useState<DragIndexState>({ active: -1, over: -1 });

    const draggableStandardColumns = React.useMemo(() => makeDraggable(standardColumns), [standardColumns]);
    const draggableCompactColumns = React.useMemo(() => makeDraggable(compactConfig.columns), [compactConfig.columns]);

    const skeletonDataSource = React.useMemo(() => {
        if (!showSkeletonInEmptyState) return tableDataSource;

        return Array.from({ length: 1 }).map((_, index) => ({
            key: `skeleton-${index}`,
            isSkeleton: true,
        }));
    }, [showSkeletonInEmptyState, tableDataSource]);

    const columnsWithSkeleton = React.useMemo(() => {
        if (!showSkeletonInEmptyState) return standardColumns;

        return standardColumns.map((col) => ({
            ...col,
            render: (value: any, record: any, index: number) => {
                if (record.isSkeleton) {
                    return <Skeleton.Input active size="small" style={{ width: '100%' }} />;
                }

                return col.render ? col.render(value, record, index) : value;
            },
        }));
    }, [showSkeletonInEmptyState, standardColumns]);

    const showHeader = !(showSkeletonInEmptyState && isCompactLayout);

    const sortableColumnKeys = React.useMemo(() => {
        if (isCompactLayout) {
            return compactConfig.columns
                .filter((col) => !('fixed' in col && col.fixed))
                .map((col) => col.key as string);
        }

        return draggableStandardColumns.filter((col) => !col.fixed).map((col) => col.key as string);
    }, [isCompactLayout, draggableStandardColumns, compactConfig.columns]);

    const sortableRowKeys = React.useMemo(() => {
        const source = isCompactLayout ? compactConfig.rows : filteredRows;
        return source.map((row) => row.key);
    }, [isCompactLayout, filteredRows, compactConfig.rows]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    const onDragEnd = React.useCallback(
        ({ active, over }: DragEndEvent) => {
            if (active.id !== over?.id) {
                const dragType = active.data.current?.type as 'column' | 'row';
                const keys = dragType === 'row' ? sortableRowKeys : sortableColumnKeys;
                const activeIndex = keys.findIndex((key) => key === active.id);
                const overIndex = keys.findIndex((key) => key === over?.id);
                if (activeIndex === -1 || overIndex === -1) return;

                const newOrder = arrayMove(keys, activeIndex, overIndex);

                if (isCompactLayout) {
                    const key = dragType === 'row' ? 'metricRowOrder' : 'destinationColumnOrder';
                    updatePreferences({ dragOrderPreferences: { compact: { [key]: newOrder } } });
                } else if (dragType === 'row') {
                    setStandardRowOrder(newOrder);
                } else {
                    updatePreferences({ dragOrderPreferences: { standard: { metricColumnOrder: newOrder } } });
                }
            }

            setDragIndex({ active: -1, over: -1 });
        },
        [sortableColumnKeys, sortableRowKeys, isCompactLayout, setStandardRowOrder, updatePreferences],
    );

    const onDragOver = React.useCallback(
        ({ active, over }: DragOverEvent) => {
            const dragType = (active.data.current?.type as 'column' | 'row') ?? 'column';
            const keys = dragType === 'row' ? sortableRowKeys : sortableColumnKeys;
            const activeIndex = keys.findIndex((key) => key === active.id);
            const overIndex = keys.findIndex((key) => key === over?.id);

            setDragIndex({
                active: active.id,
                over: over?.id,
                direction: overIndex > activeIndex ? 'right' : 'left',
                dragType,
            });
        },
        [sortableColumnKeys, sortableRowKeys],
    );

    const finalColumns = React.useMemo(() => {
        if (showSkeletonInEmptyState) return columnsWithSkeleton;
        return isCompactLayout ? draggableCompactColumns : draggableStandardColumns;
    }, [
        showSkeletonInEmptyState,
        columnsWithSkeleton,
        isCompactLayout,
        draggableCompactColumns,
        draggableStandardColumns,
    ]);

    const paginationSummaryText = React.useMemo(() => {
        if (selectedTotalCount === null || availableTotalCount === null || isLoadingCount) {
            return null;
        }

        const start = filteredRows.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
        const end = filteredRows.length > 0 ? start + filteredRows.length - 1 : 0;

        return `Showing ${start}\u2013${end} of ${selectedTotalCount.toLocaleString()} selected (${availableTotalCount.toLocaleString()} available)`;
    }, [selectedTotalCount, availableTotalCount, isLoadingCount, filteredRows.length, currentPage, pageSize]);
    const shouldShowFooter = !!paginationSummaryText || (selectedTotalCount !== null && selectedTotalCount > pageSize);

    const renderTable = () => (
        <Table
            size="small"
            pagination={false}
            showHeader={showHeader}
            rowKey={(row) => row.key}
            scroll={{ x: 'max-content' }}
            style={{ fontSize: 6, flexShrink: 0 }}
            columns={finalColumns}
            dataSource={skeletonDataSource}
            locale={{
                emptyText: <Empty description="No data" />,
            }}
            components={{
                header: { cell: TableHeaderCell },
                body: { row: TableBodyRow, cell: TableBodyCell },
            }}
        />
    );

    return (
        <div
            style={{
                width: '100%',
                maxHeight: '75vh',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            {shouldDisplayFetchFailed ?
                <Result
                    icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                    subTitle="Error fetching summary view"
                    extra={
                        <Button
                            onClick={() => {
                                setShouldDisplayFetchFailed(false);
                                refetchCurrentPage();
                            }}
                        >
                            Try again
                        </Button>
                    }
                />
            :   <>
                    {summaryViewResponse?.searchIncomplete && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 8 }}
                            message="Still indexing some releases — a few matches may be missing. Retry shortly."
                        />
                    )}
                    <div
                        style={{
                            display: isCompactLayout ? 'flex' : 'block',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'scroll',
                        }}
                    >
                        <DndContext
                            sensors={sensors}
                            onDragEnd={onDragEnd}
                            onDragOver={onDragOver}
                            collisionDetection={closestCenter}
                        >
                            <SortableContext items={sortableColumnKeys} strategy={horizontalListSortingStrategy}>
                                <SortableContext items={sortableRowKeys} strategy={verticalListSortingStrategy}>
                                    <DragIndexContext.Provider value={dragIndex}>
                                        {renderTable()}
                                    </DragIndexContext.Provider>
                                </SortableContext>
                            </SortableContext>
                        </DndContext>
                    </div>
                    {shouldShowFooter && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '20px 0 60px 0px',
                                gap: 16,
                            }}
                        >
                            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                {paginationSummaryText ?? ''}
                            </Text>
                            {selectedTotalCount !== null && selectedTotalCount > pageSize && (
                                <Pagination
                                    size="small"
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={selectedTotalCount}
                                    onChange={setCurrentPage}
                                    hideOnSinglePage
                                    showSizeChanger={false}
                                    disabled={isLoading}
                                    responsive
                                />
                            )}
                        </div>
                    )}
                </>
            }
        </div>
    );
};
